import { useState, useEffect } from "react";
import { api, ROLE_LABELS, roleClass } from "@/lib/api";
import { Newspaper, Vote, IdCard, CalendarDays, MessageCircle, Wallet, Users, LogOut, Menu, X, HandCoins, Settings as SettingsIcon, Receipt, BookOpen, Radio } from "lucide-react";
import News from "@/components/sections/News";
import Voting from "@/components/sections/Voting";
import Membership from "@/components/sections/Membership";
import CalendarSection from "@/components/sections/CalendarSection";
import LiveSection from "@/components/sections/LiveSection";
import WhatsAppSection from "@/components/sections/WhatsAppSection";
import Transparency from "@/components/sections/Transparency";
import Admin from "@/components/sections/Admin";
import Donations from "@/components/sections/Donations";
import Receipts from "@/components/sections/Receipts";
import Settings from "@/components/sections/Settings";
import Guide from "@/components/sections/Guide";
import GiacomoMascot from "@/components/GiacomoMascot";
import GiacomoChat from "@/components/GiacomoChat";
import InstallPWA from "@/components/InstallPWA";
import PushToggle from "@/components/PushToggle";

const NAV = [
  { id: "news", label: "News", icon: Newspaper },
  { id: "voting", label: "Votazioni", icon: Vote },
  { id: "membership", label: "Tesseramenti", icon: IdCard },
  { id: "donations", label: "Donazioni", icon: HandCoins },
  { id: "receipts", label: "Ricevute", icon: Receipt },
  { id: "calendar", label: "Calendario", icon: CalendarDays },
  { id: "live", label: "Riunione Live", icon: Radio },
  { id: "whatsapp", label: "Community", icon: MessageCircle },
  { id: "transparency", label: "Trasparenza", icon: Wallet },
  { id: "admin", label: "Soci", icon: Users, adminOnly: true },
  { id: "settings", label: "Impostazioni", icon: SettingsIcon, adminOnly: true },
  { id: "guide", label: "Guida", icon: BookOpen },
];

const DOC_VIEWER_ROLES = ["consiglio", "tesseramento", "segretario", "tesoriere"];

export default function Dashboard({ user, refetch }) {
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return NAV.some(n => n.id === t) ? t : "news";
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const isAdmin = user.is_admin || user.role === "consiglio" || user.role === "tesseramento";
  const canViewDocs = isAdmin || DOC_VIEWER_ROLES.includes(user.role);
  const canManageExpenses = user.is_admin || user.role === "tesoriere";
  const canManageDonations = user.is_admin || ["consiglio", "tesoriere", "tesseramento"].includes(user.role);
  const canManageVoting = user.is_admin || ["consiglio", "segretario"].includes(user.role);
  const canManageCalendar = user.is_admin || ["consiglio", "segretario"].includes(user.role);

  const [pendingDonations, setPendingDonations] = useState(0);
  useEffect(() => {
    if (!canManageDonations) return;
    api.get("/donations")
      .then(r => setPendingDonations(r.data.filter(d => !d.confirmed).length))
      .catch(() => {});
  }, [canManageDonations, tab]);

  const [pendingReceipts, setPendingReceipts] = useState(0);
  useEffect(() => {
    if (!canManageExpenses) return;
    api.get("/receipts/pending-quota")
      .then(r => setPendingReceipts(((r.data?.pending || []).length) + ((r.data?.donations || []).length)))
      .catch(() => {});
  }, [canManageExpenses, tab]);

  const navBadge = (id) => {
    const count = id === "donations" ? pendingDonations : id === "receipts" ? pendingReceipts : 0;
    if (count <= 0) return null;
    return (
      <span data-testid={`${id}-nav-badge`}
        className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[#EF3E36] text-white text-[11px] font-bold flex items-center justify-center">
        {count}
      </span>
    );
  };

  const doLogout = async () => {
    await api.post("/auth/logout");
    window.location.href = "/login";
  };

  const filteredNav = NAV.filter(n => {
    if (n.id === "admin") return canViewDocs;
    if (n.id === "settings") return isAdmin;
    if (n.id === "receipts") return canManageExpenses;
    return true;
  });

  const renderTab = () => {
    switch (tab) {
      case "news": return <News user={user} isAdmin={isAdmin} />;
      case "voting": return <Voting user={user} isAdmin={canManageVoting} />;
      case "membership": return <Membership user={user} isAdmin={isAdmin} canViewDocs={canViewDocs} refetch={refetch} />;
      case "donations": return <Donations user={user} isAdmin={canManageDonations} />;
      case "receipts": return <Receipts user={user} />;
      case "calendar": return <CalendarSection user={user} isAdmin={canManageCalendar} />;
      case "live": return <LiveSection user={user} isAdmin={canManageCalendar} />;
      case "whatsapp": return <WhatsAppSection user={user} isAdmin={isAdmin} />;
      case "transparency": return <Transparency user={user} isAdmin={isAdmin} canManageExpenses={canManageExpenses} />;
      case "admin": return <Admin user={user} canViewDocs={canViewDocs} />;
      case "settings": return <Settings />;
      case "guide": return <Guide user={user} />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="dashboard">
      {/* Sidebar desktop */}
      <aside className="hidden md:flex md:w-64 lg:w-72 flex-col border-r border-[#E8E2D8] bg-[#FAF7F2]/80 backdrop-blur-md p-6 sticky top-0 h-screen">
        <div className="flex items-center gap-3 mb-8">
          <img src="/logo.png" alt="Amunì" loading="eager" className="w-14 h-14 object-contain" />
          <div>
            <div className="font-display text-lg font-bold text-[#2B3A33] leading-tight">Amunì</div>
            <div className="text-[10px] uppercase tracking-widest text-[#6B6558]">Associazione</div>
          </div>
        </div>

        <nav className="space-y-1 flex-1">
          {filteredNav.map(n => (
            <button
              key={n.id}
              data-testid={`nav-${n.id}`}
              onClick={() => setTab(n.id)}
              className={`nav-item w-full text-left ${tab === n.id ? "active" : ""}`}
            >
              <n.icon className="w-4 h-4" />
              <span>{n.label}</span>
              {navBadge(n.id)}
            </button>
          ))}
        </nav>

        <div className="border-t border-[#E8E2D8] pt-4 mt-4">
          <div className="flex items-center gap-3 mb-3">
            {user.picture ? (
              <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-[#2B3A33] text-white flex items-center justify-center font-bold">
                {user.name?.charAt(0)?.toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-sm truncate">{user.name}</div>
              <div className={`text-[10px] px-2 py-0.5 rounded-full inline-block font-semibold uppercase tracking-wide mt-0.5 ${roleClass(user.role)}`}>
                {ROLE_LABELS[user.role]}
              </div>
            </div>
          </div>
          <div className="mb-2"><PushToggle /></div>
          <button
            data-testid="logout-btn"
            onClick={doLogout}
            className="w-full flex items-center gap-2 justify-center px-3 py-2 rounded-lg border border-[#E8E2D8] text-sm text-[#6B6558] hover:bg-white transition"
          >
            <LogOut className="w-4 h-4" />
            Esci
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-[#FAF7F2]/95 backdrop-blur border-b border-[#E8E2D8] flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="Amunì" loading="eager" className="w-11 h-11 object-contain" />
          <div className="font-display font-bold text-[#2B3A33]">Amunì</div>
        </div>
        <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen(v => !v)} className="p-2 rounded-lg border border-[#E8E2D8]">
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden fixed top-14 left-0 right-0 bottom-0 bg-[#FAF7F2] z-30 p-4 overflow-auto">
          <nav className="space-y-1">
            {filteredNav.map(n => (
              <button
                key={n.id}
                data-testid={`mobile-nav-${n.id}`}
                onClick={() => { setTab(n.id); setMobileOpen(false); }}
                className={`nav-item w-full text-left ${tab === n.id ? "active" : ""}`}
              >
                <n.icon className="w-4 h-4" />
                <span>{n.label}</span>
                {navBadge(n.id)}
              </button>
            ))}
            <div className="mt-4"><PushToggle /></div>
            <button onClick={doLogout} className="nav-item w-full text-left mt-6">
              <LogOut className="w-4 h-4" /><span>Esci</span>
            </button>
          </nav>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 min-w-0 p-4 md:p-10 pt-20 md:pt-10">
        <div className="max-w-6xl mx-auto fade-up">
          {renderTab()}
        </div>
      </main>

      <GiacomoMascot userName={user.name} onOpenChat={() => setChatOpen(true)} />
      <GiacomoChat open={chatOpen} onClose={() => setChatOpen(false)} userName={user.name} />
      {!chatOpen && (
        <button onClick={() => setChatOpen(true)} data-testid="giacomo-open-chat" title="Chiedi a Giacomo"
          className="fixed z-[65] right-4 bottom-4 sm:right-5 sm:bottom-5 w-14 h-14 rounded-full bg-white border-2 border-[#EF3E36] shadow-lg hover:scale-105 transition-transform flex items-center justify-center print:hidden">
          <img src="https://customer-assets-gfyr7b9c.emergentagent.net/job_culto-vote/artifacts/usqsv43m_GIACOMO%20-%203.PNG" alt="Chiedi a Giacomo" className="w-11 h-11 object-contain" />
        </button>
      )}
      <InstallPWA />
    </div>
  );
}
